import {
    MEDIUM_TYPE_CARD_TO_CARD,
    MEDIUM_TYPE_PHYSICAL,
    PG_PLUS_NAME,
} from '@/prisma/schemaConstants'
import { GradientText } from '@/src/common/components/marketing/heros/StyledComponents'
import LineItemDetailsCardToCard from '@/src/modules/OrderStatus/LineItemDetailsCardToCard'
import {
    CardImageContainer,
    LabelText,
    LineItemContainer,
    LineItemInfoActionContainer,
    LineItemInfoContainer,
    ProductNameText,
    TextContainer,
    ValueText,
} from '@/src/modules/OrderStatus/StyledComponents'
import LineItemDetailsPhysical from '@/src/modules/OrderStatus/content/LineItemDetailsPhysical'
import LineItemDetailsVirtual from '@/src/modules/OrderStatus/content/LineItemDetailsVirtual'
import { TLineItemSummaryProps } from '@/src/modules/OrderStatus/types'
import {
    getCustomerFacingItemStatus,
    getDeliveryStep,
} from '@/src/modules/OrderStatus/utilities'
import { DeliveryTracker } from '@/src/common/components/elements/DeliveryTracker/DeliveryTracker'
import Button from '@/src/common/components/marketing/buttons/Button'
import GiftPreview from '@/src/modules/OrderStatus/content/GiftPreview'

interface IItem {
    isCardToCard: boolean
    imbData: any[] | null
    address: string
    email: string
    phone: string
    tracking: any[]
}

const Item = ({
    isCardToCard,
    imbData,
    address = '',
    email = '',
    phone = '',
    tracking = [],
}: IItem) => {
    if (address) {
        return (
            <LineItemDetailsPhysical
                address={address}
                tracking={tracking}
                imbData={imbData}
            />
        )
    }
    if (isCardToCard) {
        return <LineItemDetailsCardToCard email={email} phone={phone} />
    }
    if (email) {
        return <LineItemDetailsVirtual email={email} />
    }
    if (phone) {
        return <LineItemDetailsVirtual phone={phone} />
    }
    return null
}

const LineItemSummary = ({
    orderStatus,
    lineItem,
    deliveryDetails,
    virtualRedemptionEmailsSent,
    isMultipleShipments,
    onClickResentEGift,
    cardUUID,
}: TLineItemSummaryProps) => {
    const {
        eGiftOpened,
        isUSPS,
        design,
        product,
        customization,
        voidedReason,
        denomination,
        refund,
        status,
        parsed_imb_data: imbData,
    } = lineItem
    const { custom, predesign, greeting_card_preview_url } = design
    const { giftBrand, giftImage, mediumType, isPgPlus } = product
    const { name, message } = customization
    const { fullyRefunded } = refund
    const { isRedeemed } = deliveryDetails
    const denomInDollars = denomination.toFixed(2)
    const imageForPhysical: string = custom ? custom : predesign
    const showDeliveryTracker: boolean =
        isUSPS && !!imbData && isMultipleShipments

    const isVoided: boolean = !!voidedReason
    const isPlastic: boolean = mediumType === MEDIUM_TYPE_PHYSICAL
    const isCardToCard: boolean = mediumType === MEDIUM_TYPE_CARD_TO_CARD

    const showResend: boolean =
        !isRedeemed && !!cardUUID && !!deliveryDetails.email

    let giftLabel: string = `${mediumType} ${giftBrand} Gift Card`
    if (isPlastic) {
        giftLabel = `${giftBrand} Gift Card`
    }
    if (isCardToCard) {
        giftLabel = isPgPlus ? PG_PLUS_NAME : `${giftBrand} eGift Card`
    }

    const finalStatus = getCustomerFacingItemStatus(
        virtualRedemptionEmailsSent,
        status,
        orderStatus,
        isPlastic,
        isVoided,
        isUSPS,
        lineItem,
        eGiftOpened,
        fullyRefunded
    )

    return (
        <LineItemContainer>
            <CardImageContainer>
                <GiftPreview
                    isPlastic={isPlastic}
                    image={isPlastic ? imageForPhysical : giftImage}
                    name={name}
                    message={message}
                    greetingCardPreviewUrl={greeting_card_preview_url}
                />
                {showDeliveryTracker && (
                    <DeliveryTracker
                        step={getDeliveryStep({
                            orderStatus: orderStatus,
                            lineItemStatus: status,
                        })}
                    />
                )}
            </CardImageContainer>
            <LineItemInfoContainer>
                <TextContainer>
                    <ProductNameText
                        capitalization={isCardToCard ? 'none' : undefined}
                        data-testid={'gift-label'}
                    >
                        {giftLabel}
                    </ProductNameText>
                </TextContainer>
                <TextContainer>
                    <LabelText>Amount: </LabelText>
                    &nbsp;
                    <ValueText data-testid={'amount'}>
                        ${denomInDollars}
                    </ValueText>
                </TextContainer>
                <TextContainer>
                    <LabelText>
                        Status:{' '}
                        <GradientText data-testid={'status'}>
                            {finalStatus}
                        </GradientText>
                    </LabelText>
                </TextContainer>
                <Item
                    isCardToCard={isCardToCard}
                    tracking={lineItem.tracking}
                    email={deliveryDetails?.email ?? ''}
                    phone={deliveryDetails?.phone ?? ''}
                    address={deliveryDetails?.latest_delivery_address ?? ''}
                    imbData={!!imbData && isMultipleShipments ? imbData : null}
                />
                {showResend && (
                    <LineItemInfoActionContainer>
                        <Button
                            size={'sm'}
                            variant={'primaryOutline'}
                            label={'Resend EGift Card'}
                            onClick={onClickResentEGift}
                        />
                    </LineItemInfoActionContainer>
                )}
            </LineItemInfoContainer>
        </LineItemContainer>
    )
}
export default LineItemSummary
